import { useQuery, useMutation, useQueryClient } from "react-query";
import taskManagerApi from "@/features/shared/services/taskManagerApi";
import { reOpenTask, updateTask, syncParentOnSubtaskReopen, } from "@/features/task/services/taskService";
import { fetchTaskMissions } from "@/features/mission/services/missionService";
import { fetchTaskCharges } from "@/features/charge/services/chargeService";
import { fetchTaskAssignees } from "@/features/assignments/services/assignmentServices";
import { fetchTaskTravels } from "@/features/travel/services/travelService";
const useTaskDetails = (taskId) => {
    const queryClient = useQueryClient();
    const { data, error, isLoading, refetch } = useQuery(["taskDetails", taskId], async () => {
        const response = await taskManagerApi.get(`/task/${taskId}`, { params: { include: 'subtasks' } });
        let task = response.data.data;
        if (task && task.id) {
            const [taskMissions, taskCharges, travels, assignees] = await Promise.all([
                fetchTaskMissions(taskId),
                fetchTaskCharges(taskId),
                fetchTaskTravels(taskId),
                fetchTaskAssignees(taskId),
            ]);
            task = { ...task, taskMissions: taskMissions, taskCharges, travels, assignees };
        }
        return task;
    }, {
        enabled: !!taskId,
        staleTime: 60000,
        keepPreviousData: true,
    });
    const updateTaskMutation = useMutation((updatedTask) => updateTask(taskId, updatedTask), {
        onSuccess: () => {
            queryClient.invalidateQueries(["taskDetails", taskId]);
        },
    });
    const reOpenTaskMutation = useMutation(({ id }) => reOpenTask(id), {
        onSuccess: async (_data, variables) => {
            const { id, parentId } = variables;
            if (parentId) {
                try {
                    await syncParentOnSubtaskReopen(parentId, id);
                    queryClient.invalidateQueries(["taskDetails", parentId]);
                }
                catch (e) {
                    console.error(e);
                }
            }
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("tasks");
            queryClient.invalidateQueries(["taskDetails", id]);
        },
    });
    return {
        task: data || null,
        loading: isLoading,
        error: error ? error.message : null,
        refetchTaskDetails: refetch,
        handleUpdateTask: updateTaskMutation.mutateAsync,
        handleReOpenTask: (id, parentId) => reOpenTaskMutation.mutateAsync({ id, parentId }),
    };
};
export default useTaskDetails;
